import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  associateDispatch as associateDispatchAction,
  clearCall as clearCallAction
} from 'src/actions/five9Actions';
import logger from 'src/utils/five9/five9Logger';

/**
 * @typedef {import('src/type-defs/five9Types').Five9Call} Five9Call
 */

const buildDispatchAssociationMessage = ({ dispatchId, callSid, five9CallId, hasServerResult }) => {
  if (!hasServerResult) return `dispatch ${dispatchId} associated with call ${callSid}`;
  if (five9CallId) return `dispatch ${dispatchId} associated with Five9 call ${five9CallId}`;
  return `dispatch ${dispatchId} linked to call ${callSid} locally; the server could not associate the Five9 call`;
};

/**
 * Tracks the active Five9 call and exposes dispatch-association helpers.
 */
export default function useCurrentFive9Call() {
  const reduxDispatch = useDispatch();

  /** @type {string|null} */
  const activeCallSid = useSelector((s) => s?.five9?.activeCallSid || null);
  /** @type {Record<string, Five9Call>} */
  const calls = useSelector((s) => s?.five9?.calls || {}, shallowEqual);

  /** @type {Five9Call|null} */
  const activeCall = activeCallSid ? calls[activeCallSid] || null : null;

  const associateDispatch = useCallback((dispatchId, callSid, options = {}) => {
    const targetSid = callSid || activeCallSid;
    if (!targetSid) return;

    reduxDispatch(associateDispatchAction(targetSid, dispatchId));

    const hasServerResult = Object.prototype.hasOwnProperty.call(options, 'five9CallId');
    const { five9CallId } = options;
    const message = buildDispatchAssociationMessage({
      dispatchId,
      callSid: targetSid,
      five9CallId,
      hasServerResult
    });

    logger.event(
      'dispatch.associated',
      {
        callSid: targetSid,
        dispatchId,
        ...(hasServerResult ? { five9CallId: five9CallId || null, serverAssociated: Boolean(five9CallId) } : {})
      },
      message
    );
  }, [reduxDispatch, activeCallSid]);

  const clearAssociation = useCallback(() => {
    if (!activeCallSid) return;
    reduxDispatch(associateDispatchAction(activeCallSid, null));
    logger.event(
      'dispatch.associated',
      { callSid: activeCallSid, dispatchId: null },
      `dispatch association cleared for call ${activeCallSid}`
    );
  }, [reduxDispatch, activeCallSid]);

  const getCallByDispatch = useCallback((dispatchId) => {
    if (dispatchId == null) return null;
    const match = Object.values(calls).find(
      (c) => c && String(c.associatedDispatchId) === String(dispatchId)
    );
    return match || null;
  }, [calls]);

  const endCall = useCallback((callSid) => {
    if (!callSid) return;
    reduxDispatch(clearCallAction(callSid));
  }, [reduxDispatch]);

  return useMemo(() => ({
    activeCall,
    calls,
    associateDispatch,
    clearAssociation,
    getCallByDispatch,
    endCall
  }), [activeCall, calls, associateDispatch, clearAssociation, getCallByDispatch, endCall]);
}
